import React, {useState, useEffect, useCallback, useRef, useMemo, memo} from 'react';
import { TouchableOpacity, View, ScrollView, FlatList } from 'react-native'


import tw from '../../../../../styles/tailwind';
import Text from '../../../../../components/Text';
import Icon from '../../../../../components/IcomoonIcon';
import { useForm } from 'react-hook-form';
import Input from '../../../../../components/Forms/Input';
import IconButton from '../../../../../components/IconButton';
import Select from '../../../../../components/Forms/Select';
import useFormSubmit from '../../../../../hooks/useFormSubmit';
import api from '../../../../../utils/api';


import { useQuery } from '@tanstack/react-query';
import OptionsSelect from '../../../../../components/Forms/OptionsSelect';
import DateSelector from '../../../../../components/Forms/DateSelector';


// Casilla de selección. state: 'all' (lleno), 'some' (parcial), 'none' (vacío).
function SelectCheckbox({ state }){
  return (
    <View style={tw.style(
      `w-5 h-5 border rounded items-center justify-center`,
      state === 'none' ? `border-gray-400 bg-white` : `border-secondary bg-primary`
    )}>
      {state === 'all' && <Icon name='check' size={12} color={tw.color('white')} />}
      {state === 'some' && <View style={tw`w-2.5 h-0.5 bg-white rounded`}></View>}
    </View>
  )
}


export function UnitsSelector({communityId, onChange, excludedUnits=[]}){
  const [selectedUnits, setSelectedUnits] = useState<Array<Unit>>([]);
  const [expandedStreets, setExpandedStreets] = useState({});

  const { isLoading, error, data: units, refetch: refetchUnits } = useQuery({
    queryKey: ['community.units', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units`).then(response => response.data),
  })

  const _units = useMemo(() => {
    return units?.filter(unit => ! excludedUnits?.some(excl => excl === unit.uuid))
  }, [units, excludedUnits])

  function emit(newValue){
    setSelectedUnits(newValue)
    onChange(newValue)
  }

  function isSelected(unit){
    return selectedUnits.some(e => e.uuid === unit.uuid)
  }

  function toggleUnit(unit:Unit){
    if(isSelected(unit)){
      emit(selectedUnits.filter(e => e.uuid !== unit.uuid))
    }else{
      emit([...selectedUnits, unit])
    }
  }

  // Selecciona / deselecciona toda una calle.
  function toggleStreet(streetUnits){
    const allSelected = streetUnits.every(u => isSelected(u))
    if(allSelected){
      const ids = new Set(streetUnits.map(u => u.uuid))
      emit(selectedUnits.filter(e => !ids.has(e.uuid)))
    }else{
      const missing = streetUnits.filter(u => !isSelected(u))
      emit([...selectedUnits, ...missing])
    }
  }

  function toggleExpand(street){
    setExpandedStreets(prev => ({ ...prev, [street]: !prev[street] }))
  }

  // Agrupar por calle.
  const streetGroups = useMemo(() => {
    const groups = {}
    _units?.forEach(u => {
      const street = u.street_name || 'Sin calle'
      if (!groups[street]) groups[street] = []
      groups[street].push(u)
    })
    return Object.keys(groups).sort().map(street => ({ street, units: groups[street] }))
  }, [_units])

  // Filas planas: encabezado de calle + viviendas si está expandida.
  const listData = useMemo(() => {
    const rows = []
    streetGroups.forEach(({ street, units }) => {
      rows.push({ type: 'street', street, units, key: `street:${street}` })
      if (expandedStreets[street]) {
        units.forEach(u => rows.push({ type: 'unit', unit: u, key: u.uuid }))
      }
    })
    return rows
  }, [streetGroups, expandedStreets])

  const renderItem = ({ item }) => {
    if (item.type === 'street') {
      const selCount = item.units.filter(isSelected).length
      const groupState = selCount === 0 ? 'none' : selCount === item.units.length ? 'all' : 'some'
      const expanded = !!expandedStreets[item.street]

      return (
        <View style={tw`row px-3 h-13 bg-gray-100 border-b border-gray-200`}>
          <TouchableOpacity style={tw`py-3 pr-3`} onPress={() => toggleStreet(item.units)}>
            <SelectCheckbox state={groupState} />
          </TouchableOpacity>

          <TouchableOpacity style={tw`flex-1 row justify-between items-center py-3`} onPress={() => toggleExpand(item.street)}>
            <Text style={tw`text-secondary font-600`}>{item.street}</Text>

            <View style={tw`row items-center`}>
              <Text style={tw`text-gray-400 mr-3`}>{selCount}/{item.units.length}</Text>
              <Icon
                name='chevron-right'
                size={11}
                color={tw.color('secondary')}
                style={{ transform: [{ rotate: expanded ? '90deg' : '0deg' }] }}
              />
            </View>
          </TouchableOpacity>
        </View>
      )
    }

    const unit = item.unit
    const sel = isSelected(unit)

    return (
      <TouchableOpacity style={tw`row items-center px-3 pl-10 h-13 border-b border-gray-100`} onPress={() => toggleUnit(unit)}>
        <SelectCheckbox state={sel ? 'all' : 'none'} />
        <Text style={tw`ml-3 text-secondary`}>{unit.name}</Text>
      </TouchableOpacity>
    )
  }

  return (
    <View style={tw`flex-1`}>
      <FlatList
        data={listData}
        extraData={selectedUnits}
        renderItem={renderItem}
        keyExtractor={item => item.key}
      />

      {
        isLoading &&
          <Text style={tw`text-gray-300 text-center py-10`}>Cargando viviendas...</Text>
      }
    </View>
  )
}


function PeriodicFeeUnitRow({ index, item }) {
  return (
    <View
      style={tw.style(`row justify-between px-4 min-h-15`, index % 2 == 1 && `bg-gray-100`)}
    >
      <View style={tw`flex-1`}>
        <Text style={tw`text-secondary`}>{item.name}</Text>

        {
          item.owners?.length > 0 &&
            <Text style={tw`-mt-1 text-gray-400`}>{item.owners[0].name}</Text>
        }
      </View>

      <View style={tw`items-end`}>
        {
          item.fee_type ?
            <>
              <Text style={tw`text-gray-500`}>${item.fee_type.amount}</Text>
              <Text style={tw`-mt-1 text-gray-500`}>{item.fee_type.name}</Text>
            </>
            :
            <Text style={tw`text-red-400`}>Sin cuota</Text>
        }
      </View>
    </View>
  )
}

function PeriodicFeeUnits({communityId}){
  const { isLoading, error, data: units, refetch: refetchUnits } = useQuery({
    queryKey: ['community.units', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units?with=fee_type`).then(response => response.data),
  })

  const _units = useMemo(() => {
    return units?.filter(unit => !!unit.fee_type)
  }, [units])



  return (
    <View style={tw`flex-1`}>
      <View style={tw`bg-gray-100 px-4 py-2 row justify-between mb-3`}>
        <Text style={tw`text-gray-400`}>Vivienda</Text>
        <Text style={tw`text-gray-400`}>Monto / Tipo</Text>
      </View>

      {
        (isLoading) &&
          <Text style={tw`mt-6 text-gray-400 text-center`}>Cargando...</Text>
      }

      <FlatList
        data={_units}
        contentContainerStyle={tw`gap-y-3 pb-2`}
        renderItem={ ({item, index}) =>
          <PeriodicFeeUnitRow
            key={item.uuid}
            index={index}
            item={item}
          />
        }
        keyExtractor={item => item.uuid}
      />
    </View>
  )
}


function FeeTypeSelector({ communityId, onTypeSelected }){
  const [selectedFeeType, setSelectedFeeType] = useState();

  const { isLoading, data: feeTypes, refetch } = useQuery({
    queryKey: ['community.fees.types', communityId],
    queryFn: () => api.get(`/communities/${communityId}/fees/types`).then(response => response.data),
  })

  const filteredTypes = useMemo(()=>{
    return feeTypes?.filter(e => !e.is_variant) ?? []
  }, [feeTypes])

  const onChange = (feeType) => {
    setSelectedFeeType(feeType)
    onTypeSelected(feeType)
  }

  return (
    <View>
      {/* <Text style={tw``}>Tipo de adeudo</Text> */}

      <View style={tw`gap-y-3`}>
        {
          filteredTypes?.map((feeType) => {
            const isSelected = selectedFeeType?.uuid == feeType.uuid
            const color = feeType.type == "main" ? 'secondary' : 'primary'
            // console.log({ feeType });
            

            return (
              <TouchableOpacity
                key={feeType.uuid}
                onPress={() => onChange(feeType)}
                style={tw.style(`px-4 py-3 row justify-between items-start rounded-lg bg-gray-100 `, isSelected && `border border-${color}`)}
              >
                <View style={tw``}>
                  <Text style={tw`text-${color} font-500`}>{feeType.name}</Text>

                  <Text style={tw`text-gray-400 text-xs`}>{
                    feeType.type == "main" ?
                      "Cuota principal"
                    : 
                      "Otro tipo de adeudo"
                  }</Text>
                </View>

                <View style={tw`flex mt-1 h-4.5 w-4.5 rounded items-center justify-center bg-white border border-gray-200`}>
                  {
                    isSelected &&
                      <View style={tw.style(`h-3 w-3 rounded`, `bg-${color}`)}></View>
                  }
                </View>
              </TouchableOpacity>
            )
          })
        }
      </View>
    </View>
  )

}


//////////////
//////////////
import BottomSheet from '../.././../../../components/BottomSheet';
import { MonthSelector, MonthSelectorDropdown } from '../../../../../components/Forms/MonthSelector';
import moment from 'moment';


function MonthSelectorSection({onChange}){
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [selecting, setSelecting] = useState(false);
 
  useEffect(() => {
    onChange(selectedMonth)
  }, [selectedMonth]);

  return (
    <View style={tw``}>
      <View style={tw`row justify-between`}>
        <Text style={tw`text-gray-500`}>Mes</Text>

        <MonthSelectorDropdown
          selectedDate={selectedMonth}
          onPress={() => setSelecting(true)}
        />
      </View>

      {
        selecting &&
          <MonthSelector
            selectedDate={selectedMonth}
            maxDate={moment().add(2, 'year')}
            onChange={date => {
              setSelecting(false)
              setSelectedMonth(date)
            }}
          />
      }
    </View>
  )
}
//////////////
//////////////





export default function CreateFeeModal({ onRequestClose, visible, onFeeCreated, communityId }) {
  const { control, handleSubmit, watch, setValue, reset } = useForm();
  const feeType = watch("type")
  const applyMonth = watch("apply_month")
  const target = watch("target")

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: _onFeeCreated,
    onError: (error) => { },
    successToastMessage: "Cuota creada",
    endpoint: `/communities/${communityId}/fees`,
  })



  useEffect(() => {
    reset({
      type: null,
      target: "all",
      apply_month: new Date(),
    })
  }, []);


  useEffect(() => {
    if (target === "all") {
      setValue("units", null)
    }
  }, [target]);

  useEffect(() => {
    if (!visible) reset({})
  }, [visible]);


  useEffect(() => {
    const months = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"]
    const capitalize = s => s && s[0].toUpperCase() + s.slice(1)

    if(feeType?.type === "main"){
      const monthName = capitalize(applyMonth?.toLocaleString('es', { month: 'long' }))
      setValue('name', `${feeType.name} ${monthName} ${applyMonth?.getFullYear()}`)
    }else{
      setValue('name', ``)
    }
  }, [applyMonth, feeType]);



  function _onFeeCreated(feeResponse) {
    console.log("_onfeeCreated", feeResponse);
    onFeeCreated?.(feeResponse)
  }


  const _submitForm = (data) => {
    const _data = {
      ...data,
      type: data.type?.uuid,
    }
    
    submitForm(_data)
  }



  return (
    <BottomSheet
      // height={feeType?.type === "other" && target == "all" ? "80%" : "95%"}
      height={feeType?.type === "other" && target == "all" ? 650 : 800} //! 
      visible={visible}
      onRequestClose={onRequestClose}
      showCloseButton={true}
    >
      <View style={tw`px-5 flex-1`}>
        {/* Header */}
        <View style={tw``}>
          <Text style={tw`mb-4 mt-5 text-lg font-600 text-secondary`}>Crear adeudo</Text>

          <TouchableOpacity
            style={tw`absolute top-5 right-2 items-center justify-center`}
            onPress={onRequestClose}
          >
            <Icon name='close' style={tw`text-red`} size={26} />
          </TouchableOpacity>
        </View>



        
        <View style={tw`flex-1 -mx-5 px-5`}>
          {/*
            Seleccion de tipo de adeudo
          */}
          <FeeTypeSelector
            communityId={communityId}
            onTypeSelected={feeType => setValue("type", feeType)}
          />



          {/*
            Mes
          */}
          <View style={tw`mt-6`}>
            <MonthSelectorSection
              onChange={date => setValue('apply_month', date)}
            />
          </View>
          


          {/*
            Descripción
          */}
          <View style={tw`mt-5`}>
            <Text style={tw`text-gray-500`}>Concepto</Text>
            <Input
              control={control}
              name='name'
              placeholder={feeType?.type === "main" ? 'ej: Mantenimiento Noviembre 2023' : 'ej: Arreglo de reja de entrada'}
              rules={{required:true}}

            />
          </View>



          {/*
            Monto (solo se muestra cuando no es cuota principal)
          */}
          {
            feeType?.type === "other" && 
              <View style={tw`row justify-between`}>
                <Text style={tw`text-gray-500`}>Monto</Text>

                <View style={tw`row`}>
                  <Input
                    control={control}
                    name='amount'
                    placeholder='' style={{ input: `w-50 pl-20`}}
                    rules={{required:true}}
                    keyboardType="numeric"
                  />
                  <Text style={tw`absolute left-16 text-gray-500 top-4 `}>$</Text>
                </View>
              </View>
          }

          <View style={tw`mb-4`}>
            <Text style={tw`text-gray-500`}>Viviendas a la que se cobrará</Text>

            <OptionsSelect
              control={control}
              name='target'
              value={"all"}
              options={[
                { value: "all", label: "Todas" },
                { value: "selected", label: "Elegir viviendas" },
              ]}
            />
          </View>

          {
            (target === "all" && feeType?.type === "main") &&
              // <Text style={tw`text-gray-600 font-500`}>El monto a cobrar a cada vivienda será tomado de la configuración de cuotas mensuales</Text>
              <PeriodicFeeUnits communityId={communityId} />
          }

          {
            target == "selected" &&
              <UnitsSelector communityId={communityId} onChange={units => setValue("units" , units.map(unit => unit.uuid))} />
          }
        </View>


        <IconButton
          title={isSubmitting ? "Creando..." : "Crear adeudo"}
          style={tw`self-end mb-5`}
          disabled={isSubmitting}
          onPress={handleSubmit(_submitForm)}
        />

      </View>
    </BottomSheet>
  );
}
